import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function AuthorityReportInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [pastReports, setPastReports] = useState([]);
  const [form, setForm] = useState({ summary: '', challenges: '', actions_taken: '', recommendations: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStats = async () => {
    try {
      const res = await api.get('/authority-reports/stats', { params: { month, year } });
      setStats(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load stats');
    }
  };

  const fetchPast = async () => {
    try {
      const res = await api.get('/authority-reports/my');
      setPastReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    Promise.all([fetchStats(), fetchPast()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { fetchStats(); }, [month, year]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) { setError('Summary is required'); return; }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      await api.post('/authority-reports/submit', { ...form, month, year });
      setSuccess('Report submitted successfully');
      fetchPast();
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (st) => {
    if (st === 'reviewed') return { bg: 'rgba(16,185,129,0.2)', color: '#6ee7b7' };
    if (st === 'acknowledged') return { bg: 'rgba(99,102,241,0.2)', color: '#a5b4fc' };
    return { bg: 'rgba(245,158,11,0.2)', color: '#fcd34d' };
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="Monthly Report" subtitle="Submit your monthly activity report"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)} />
          <div style={s.content}>
            <PageHeader icon="📊" title="Monthly Report" />

            {/* Period selector */}
            <div style={{ ...s.card, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Report Period:</span>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...s.input, width: 140 }}>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...s.input, width: 100 }}>
                {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {stats?.already_submitted && (
                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'rgba(245,158,11,0.2)', color: '#fcd34d', border: '1px solid rgba(245,158,11,0.4)' }}>
                  Already submitted — resubmitting will overwrite
                </span>
              )}
            </div>

            {/* Stats preview */}
            {stats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  ['Assigned', stats.total_assigned, COLORS.purple],
                  ['Resolved', stats.total_resolved, COLORS.green],
                  ['Rejected', stats.total_rejected, COLORS.red || '#ef4444'],
                  ['Overdue', stats.total_overdue, COLORS.amber],
                  ['Avg Days', `${stats.avg_resolution_days}d`, COLORS.cyan],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ ...s.card, padding: '14px 16px', textAlign: 'center', border: `1px solid ${color}33` }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Form */}
            <div style={{ ...s.card, padding: '24px 28px', marginBottom: 28 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 20 }}>
                {MONTHS[month-1]} {year} Report
              </div>
              {error && <div style={{ ...s.alertErr, marginBottom: 14 }}>{error}</div>}
              {success && <div style={{ ...s.alertOk, marginBottom: 14 }}>{success}</div>}
              <form onSubmit={handleSubmit}>
                {[
                  { key: 'summary', label: 'Summary *', placeholder: 'Summarize your activity this month...' },
                  { key: 'challenges', label: 'Challenges', placeholder: 'Any challenges faced...' },
                  { key: 'actions_taken', label: 'Actions Taken', placeholder: 'Key actions taken...' },
                  { key: 'recommendations', label: 'Recommendations', placeholder: 'Recommendations for improvement...' },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 6 }}>{label}</label>
                    <textarea value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      rows={key === 'summary' ? 4 : 3} style={{ ...s.input, resize: 'vertical' }} placeholder={placeholder} />
                  </div>
                ))}
                <button type="submit" className="ur-btn-hover" style={{ ...s.btnPrimary, padding: '10px 28px' }} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </form>
            </div>

            {/* Past reports */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Past Reports</div>
            {!pastReports.length ? <EmptyState text="No past reports" sub="Your submitted reports will appear here" /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pastReports.map(r => {
                  const sc = statusColor(r.status);
                  return (
                    <div key={r.id} style={{ ...s.card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{MONTHS[r.month-1]} {r.year}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                          {r.total_resolved} resolved · {r.total_assigned} assigned · {r.avg_resolution_days}d avg
                        </div>
                        {r.reviewer_note && <div style={{ fontSize: 11, color: '#a5b4fc', marginTop: 4 }}>Note: {r.reviewer_note}</div>}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color }}>
                        {r.status.toUpperCase()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthorityReport() {
  return <ThemeProvider><AuthorityReportInner /></ThemeProvider>;
}
